import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceArea
} from 'recharts';
import measurementService from '../../../../services/measurementService';
import patientService from '../../../../services/patientService';
import { toast } from 'react-toastify';
import {
  DEFAULT_MEASUREMENT_QUERY,
  applyMeasurementFromDateChange,
  getMeasurementQueryMaxDate,
  isFromToIncomplete,
  isMeasurementQueryDirty,
  patchOnQueryModeChange,
  sanitizeMeasurementToDate,
} from '../../../../utils/queryPeriodFilters';
import AppDateInput from '../../../../components/Common/AppDateInput/AppDateInput';
import './MeanChart.css';

const MeanChart = ({ practiceId, patientId, measurements: measurementsProp = null, scheduleTargets: scheduleTargetsProp = null, embedded = false }) => {
  const navigate = useNavigate();
  const [queryType, setQueryType] = useState(DEFAULT_MEASUREMENT_QUERY.queryType);
  const [dateRange, setDateRange] = useState(DEFAULT_MEASUREMENT_QUERY.dateRange);
  const [fromDate, setFromDate] = useState(DEFAULT_MEASUREMENT_QUERY.fromDate);
  const [toDate, setToDate] = useState(DEFAULT_MEASUREMENT_QUERY.toDate);
  const [patientData, setPatientData] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [scheduleTargets, setScheduleTargets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queryLoading, setQueryLoading] = useState(false);

  const effectiveMeasurements = (measurementsProp && measurementsProp.length > 0) ? measurementsProp : measurements;
  const effectiveScheduleTargets = scheduleTargetsProp || scheduleTargets;

  // Fetch patient data and schedule targets on mount
  useEffect(() => {
    if (practiceId && patientId) {
      fetchPatientData();
      if (!scheduleTargetsProp) fetchScheduleTargets();
    }
  }, [practiceId, patientId, scheduleTargetsProp]);

  // Fetch measurements on initial load / patient change only if measurementsProp is not provided
  useEffect(() => {
    if (practiceId && patientId && (!measurementsProp || measurementsProp.length === 0)) {
      fetchMeasurements();
    } else {
      setLoading(false);
    }
  }, [practiceId, patientId, measurementsProp]);

  const fetchPatientData = async () => {
    try {
      const response = await patientService.getMeasurementHeaderFast(practiceId, patientId);
      if (response.data.success) {
        setPatientData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
      toast.error('Failed to load patient data');
    }
  };

  const fetchScheduleTargets = async () => {
    try {
      const data = await patientService.getResolvedScheduleTargetsForPatient(practiceId, patientId);
      setScheduleTargets(data);
    } catch (error) {
      console.error('Error fetching schedule targets:', error);
    }
  };

  const fetchMeasurements = async () => {
    try {
      setQueryLoading(true);
      let fromDateParam, toDateParam;
      const isAllHistory = queryType === 'dateRange' && String(dateRange).toUpperCase() === 'ALL';

      if (queryType === 'dateRange') {
        if (!isAllHistory) {
          const days = parseInt(dateRange.replace(/\D/g, ''), 10) || 30;
          const endDate = new Date();
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - days);

          const fmtDate = (d) => {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
          };

          fromDateParam = fmtDate(startDate);
          toDateParam = fmtDate(endDate);
        }
      } else {
        if (fromDate && toDate) {
          if (fromDate.includes('/')) {
            const [fromMonth, fromDay, fromYear] = fromDate.split('/');
            const [toMonth, toDay, toYear] = toDate.split('/');
            fromDateParam = `${fromYear}-${fromMonth.padStart(2, '0')}-${fromDay.padStart(2, '0')}`;
            toDateParam = `${toYear}-${toMonth.padStart(2, '0')}-${toDay.padStart(2, '0')}`;
          } else {
            fromDateParam = fromDate;
            toDateParam = toDate;
          }
        }
      }

      if (isAllHistory || (fromDateParam && toDateParam)) {
        const response = await measurementService.getBloodGlucoseByDateRange(
          practiceId,
          patientId,
          isAllHistory ? undefined : fromDateParam,
          isAllHistory ? undefined : toDateParam,
          'daily_average'
        );

        if (response.data?.success) {
          setMeasurements(response.data.data || []);
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching measurements:', error);
      if (error.response?.status === 404) {
        console.log('Measurement API endpoint not yet available');
        setMeasurements([]);
      } else {
        toast.error('Failed to load measurements');
        setMeasurements([]);
      }
      setLoading(false);
    } finally {
      setQueryLoading(false);
    }
  };

  const handleQueryTypeChange = (nextType) => {
    const patch = patchOnQueryModeChange(nextType);
    setQueryType(patch.queryType);
    if (Object.prototype.hasOwnProperty.call(patch, 'fromDate')) {
      setFromDate(patch.fromDate);
      setToDate(patch.toDate);
    }
  };

  const handleQuery = () => {
    if (isFromToIncomplete(queryType, fromDate, toDate)) {
      toast.warning('Please select both start and end dates');
      return;
    }
    fetchMeasurements();
  };

  const handleReset = () => {
    setQueryType(DEFAULT_MEASUREMENT_QUERY.queryType);
    setDateRange(DEFAULT_MEASUREMENT_QUERY.dateRange);
    setFromDate(DEFAULT_MEASUREMENT_QUERY.fromDate);
    setToDate(DEFAULT_MEASUREMENT_QUERY.toDate);
  };

  const isQueryDisabled = queryLoading || isFromToIncomplete(queryType, fromDate, toDate);
  const isResetDisabled = queryLoading || !isMeasurementQueryDirty({
    queryType, dateRange, fromDate, toDate, sortBy: DEFAULT_MEASUREMENT_QUERY.sortBy,
  });

  const patientName = patientData?.patient 
    ? `${patientData.patient.first_name || ''} ${patientData.patient.last_name || ''}`.trim()
    : 'Loading...';
  const patientDOB = patientData?.patient?.date_of_birth 
    ? new Date(patientData.patient.date_of_birth).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    : 'Loading...';

  // Get period names from schedule targets
  const periodNames = useMemo(() => {
    if (!effectiveScheduleTargets?.bloodGlucoseTimePeriod) {
      return ['Wake-up Time', 'Before-Breakfast', 'After-Breakfast', 'Before-Lunch', 'After-Lunch', 'Before-Dinner', 'After-Dinner', 'Bedtime', 'Midnight'];
    }
    return effectiveScheduleTargets.bloodGlucoseTimePeriod.map(p => p.name);
  }, [effectiveScheduleTargets]);

  // Determine period from measurement
  const determinePeriodFromMeasurement = (measurement) => {
    const raw = String(
      measurement.period_name ||
      measurement.period ||
      measurement.measure_note ||
      measurement.note ||
      ''
    ).toUpperCase().replace(/[\s\-_]/g, '');

    if (raw.includes('WAKE')) return 'Wake-up Time';
    if (raw.includes('BEFOREBREAKFAST') || raw === 'BEFOREMEAL') return 'Before-Breakfast';
    if (raw.includes('AFTERBREAKFAST') || raw === 'AFTERMEAL') return 'After-Breakfast';
    if (raw.includes('BEFORELUNCH')) return 'Before-Lunch';
    if (raw.includes('AFTERLUNCH')) return 'After-Lunch';
    if (raw.includes('BEFOREDINNER')) return 'Before-Dinner';
    if (raw.includes('AFTERDINNER')) return 'After-Dinner';
    if (raw.includes('BEDTIME') || raw.includes('BED')) return 'Bedtime';
    if (raw.includes('MIDNIGHT') || raw.includes('NIGHT')) return 'Midnight';
    if (raw.includes('BEFORE')) return 'Before-Breakfast';
    if (raw.includes('AFTER')) return 'After-Breakfast';

    const timeStr = measurement.measurement_time || measurement.time_recorded || '';
    if (timeStr) {
      const parts = String(timeStr).split(':');
      const hour = parseInt(parts[0], 10);
      const min = parseInt(parts[1] || '0', 10);
      const decimalHour = hour + min / 60;
      if (!isNaN(decimalHour)) {
        if (decimalHour >= 6 && decimalHour < 7) return 'Wake-up Time';
        if (decimalHour >= 7 && decimalHour < 8) return 'Before-Breakfast';
        if (decimalHour >= 8 && decimalHour < 11) return 'After-Breakfast';
        if (decimalHour >= 11 && decimalHour < 13) return 'Before-Lunch';
        if (decimalHour >= 13 && decimalHour < 17) return 'After-Lunch';
        if (decimalHour >= 17 && decimalHour < 19) return 'Before-Dinner';
        if (decimalHour >= 19 && decimalHour < 20.5) return 'After-Dinner';
        if (decimalHour >= 20.5 && decimalHour < 22) return 'Bedtime';
        return 'Midnight';
      }
    }
    return 'Wake-up Time';
  };

  // Calculate mean values by period
  const periodMeans = useMemo(() => {
    if (!effectiveMeasurements || effectiveMeasurements.length === 0) {
      return periodNames.map(name => ({ name, mean: null }));
    }

    return periodNames.map(periodName => {
      const periodMeasurements = effectiveMeasurements.filter(m => {
        const determinedPeriod = determinePeriodFromMeasurement(m);
        return determinedPeriod === periodName;
      });

      if (periodMeasurements.length === 0) {
        return { name: periodName, mean: null };
      }

      const bgValues = periodMeasurements
        .map(m => parseFloat(m.blood_glucose_value_1 ?? m.blood_glucose_value))
        .filter(v => !isNaN(v));

      const mean = bgValues.length > 0 
        ? bgValues.reduce((a, b) => a + b, 0) / bgValues.length 
        : null;

      return { name: periodName, mean };
    });
  }, [effectiveMeasurements, periodNames]);

  const chartData = useMemo(() => {
    return periodMeans.map((p) => ({
      name: p.name,
      Glucose: p.mean != null ? Math.round(p.mean) : null
    }));
  }, [periodMeans]);

  const formatEmbeddedPeriod = (value) => {
    const labels = {
      'Wake-up Time': 'Wake-up',
      'Before-Breakfast': 'Pre-Bfast',
      'After-Breakfast': 'Post-Bfast',
      'Before-Lunch': 'Pre-Lunch',
      'After-Lunch': 'Post-Lunch',
      'Before-Dinner': 'Pre-Dinner',
      'After-Dinner': 'Post-Dinner'
    };
    return labels[value] || value;
  };

  return (
    <div className={`bg-mean-chart-page${embedded ? ' bg-mean-chart-page--embedded' : ''}`} style={embedded ? { background: "transparent", backgroundColor: "transparent", padding: 0, minHeight: "auto" } : {}}>
      {/* Header Section */}
      {!embedded && (
      <div className="bgmc-header">
        <button className="bgmc-nav-button active">Blood Glucose Chart</button>
        <div className="bgmc-patient-info">
          <div className="bgmc-info-row">
            <span className="bgmc-label">Name :</span>
            <span className="bgmc-value">{patientName}</span>
          </div>
          <div className="bgmc-info-row">
            <span className="bgmc-label">DOB :</span>
            <span className="bgmc-value">{patientDOB}</span>
          </div>
        </div>
      </div>
      )}

      {/* Query Period Section */}
      {!embedded && (
      <div className="bgmc-query-section">
        <h4>Query Period</h4>
        <div className="bgmc-radio-group">
          <label className="bgmc-radio-label">
            <input
              type="radio"
              name="queryType"
              value="dateRange"
              checked={queryType === 'dateRange'}
              onChange={(e) => handleQueryTypeChange(e.target.value)}
            />
            <span>Date Range</span>
            {queryType === 'dateRange' && (
              <select 
                className="bgmc-dropdown"
                value={dateRange}
                onChange={(e) => {
                  const value = e.target.value;
                  setDateRange(value);
                  if (String(value).toUpperCase() === 'ALL') {
                    setFromDate('');
                    setToDate('');
                  }
                }}
              >
                <option>Last 30 days</option>
                <option>Last 7 days</option>
                <option>Last 14 days</option>
                <option>Last 60 days</option>
                <option>Last 90 days</option>
                <option>All</option>
              </select>
            )}
          </label>
          <label className="bgmc-radio-label">
            <input
              type="radio"
              name="queryType"
              value="from"
              checked={queryType === 'from'}
              onChange={(e) => handleQueryTypeChange(e.target.value)}
            />
            <span>From</span>
            {queryType === 'from' && (
              <div className="bgmc-date-inputs">
                <div className="bgmc-date-wrapper">
                  <AppDateInput
                    className="bgmc-date-input"
                    value={fromDate}
                    max={getMeasurementQueryMaxDate()}
                    onChange={(nextFrom) => {
                      const next = applyMeasurementFromDateChange(nextFrom, toDate);
                      setFromDate(next.fromDate);
                      setToDate(next.toDate);
                    }}
                  />
                  <FontAwesomeIcon icon={faCalendarAlt} className="bgmc-calendar-icon" />
                </div>
                <span className="bgmc-date-separator">to</span>
                <div className="bgmc-date-wrapper">
                  <AppDateInput
                    className="bgmc-date-input"
                    value={toDate}
                    min={fromDate || undefined}
                    max={getMeasurementQueryMaxDate()}
                    onChange={(nextTo) => setToDate(sanitizeMeasurementToDate(nextTo, fromDate))}
                  />
                  <FontAwesomeIcon icon={faCalendarAlt} className="bgmc-calendar-icon" />
                </div>
              </div>
            )}
          </label>
        </div>
        <div className="bgmc-action-buttons">
          <button className="bgmc-action-btn" onClick={handleQuery} disabled={isQueryDisabled}>
            {queryLoading ? 'Loading...' : 'Query'}
          </button>
          <button className="bgmc-action-btn" onClick={handleReset} disabled={isResetDisabled}>
            Reset
          </button>
          <button 
            className="bgmc-action-btn"
            onClick={() => navigate(`/practices/${practiceId}/patients/${patientId}/measurements/blood-glucose/chart`)}
          >
            Day Chart
          </button>
          <button className="bgmc-action-btn">Print</button>
          <button className="bgmc-action-btn">Export</button>
          <button className="bgmc-action-btn">Email</button>
        </div>
      </div>
      )}

      {/* Mean Chart Section */}
      <div className="bgmc-chart-section" style={embedded ? { border: "none", padding: 0, background: "transparent" } : {}}>
        {!embedded && <div className="bgmc-chart-title-top-left">Mean Chart of Blood Glucose</div>}
        {!embedded && <div className="bgmc-chart-title-center">Measurement Period Mean Chart of Blood Glucose</div>}
        <div className="bgmc-chart-container">
          {!embedded && (
            <div className="bgmc-chart-legend" style={{ justifyContent: "flex-end", marginBottom: "10px" }}>
              <div className="bgmc-legend-item">
                <div className="bgmc-legend-dot bgmc-legend-glucose" style={{ background: "#0077b6" }}></div>
                <span style={{ fontSize: "12px", fontWeight: 600, color: "#334155" }}>Glucose (mg/dL)</span>
              </div>
            </div>
          )}
          <div
            className="bgmc-chart-area"
            style={embedded
              ? { border: "none", borderRadius: 0, background: "transparent", padding: 0, minHeight: "260px" }
              : { border: "1px solid #e2e8f0", borderRadius: "8px", background: "#ffffff", padding: "16px 12px 10px 12px" }}
          >
            {loading || queryLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: "#64748b" }}>Loading chart data...</div>
            ) : periodMeans.every(p => p.mean == null) ? (
              <div style={{ textAlign: 'center', padding: '40px', color: "#64748b" }}>No data available for the selected period</div>
            ) : (
              <ResponsiveContainer width="100%" height={embedded ? 260 : 290}>
                <LineChart data={chartData} margin={embedded
                  ? { top: 10, right: 16, left: 8, bottom: 5 }
                  : { top: 20, right: 25, left: 15, bottom: 65 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={embedded ? "#e2e8f0" : "#f1f5f9"} />
                  <XAxis
                    dataKey="name"
                    interval={embedded ? 'preserveStartEnd' : 0}
                    angle={embedded ? 0 : -30}
                    textAnchor={embedded ? "middle" : "end"}
                    height={embedded ? 30 : 65}
                    tick={embedded
                      ? { fontSize: 8, fill: "#718096" }
                      : { fontSize: 11, fill: "#475569", fontWeight: 500 }}
                    tickFormatter={embedded ? formatEmbeddedPeriod : undefined}
                  />
                  <YAxis
                    width={embedded ? 48 : undefined}
                    domain={embedded ? [60, 300] : [0, 400]}
                    ticks={embedded ? undefined : [0, 50, 100, 150, 200, 250, 300, 350, 400]}
                    tick={embedded
                      ? { fontSize: 10, fill: "#718096" }
                      : { fontSize: 11, fill: "#475569" }}
                    label={{
                      value: embedded ? "mg/dL" : "Blood Glucose (mg/dL)",
                      angle: -90,
                      position: "insideLeft",
                      offset: 0,
                      style: embedded
                        ? { fontSize: "10px", fill: "#718096" }
                        : { fontSize: 11, fontWeight: 600, fill: "#334155", textAnchor: "middle" }
                    }}
                  />
                  <Tooltip
                    contentStyle={{ background: "#ffffff", borderRadius: "8px", border: "1px solid #cbd5e1", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", fontSize: "12px" }}
                    formatter={(val) => [val != null ? `${val} mg/dL` : "No reading", "Glucose"]}
                  />
                  <ReferenceArea
                    y1={70}
                    y2={140}
                    fill="#f0fdf4"
                    fillOpacity={0.85}
                    stroke="#bbf7d0"
                    strokeDasharray="2 2"
                    label={{ value: "Target Range (70–140 mg/dL)", position: "insideTopRight", fill: "#16a34a", fontSize: 10, fontWeight: 600 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Glucose"
                    stroke={embedded ? "#00b6c6" : "#0077b6"}
                    strokeWidth={embedded ? 2 : 2.5}
                    dot={embedded
                      ? { fill: "#00b6c6", r: 4 }
                      : { r: 5, fill: "#0077b6", stroke: "#ffffff", strokeWidth: 2 }}
                    activeDot={{ r: embedded ? 6 : 7 }}
                    connectNulls={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeanChart;

